/**
 * Доменные енамы. Держим их здесь, а не только в Prisma, потому что
 * фронт должен знать те же значения (статусы сцен, типы, форматы).
 */

/** Статусы джобы (§44). */
export const JobStatus = {
  QUEUED: 'queued',
  PREPARING: 'preparing',
  SUBMITTED: 'submitted',
  PROCESSING: 'processing',
  POSTPROCESSING: 'postprocessing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

/** Три этапа, которые видит пользователь (§68). Технические статусы наружу не выходят. */
export const GenerationStage = {
  PREPARING: 'preparing',
  CREATING: 'creating',
  FINALIZING: 'finalizing',
} as const;
export type GenerationStage = (typeof GenerationStage)[keyof typeof GenerationStage];

export const generationStageLabels: Record<GenerationStage, string> = {
  preparing: 'Подготавливаем сцену',
  creating: 'Создаём',
  finalizing: 'Финальная обработка',
};

/** Маппинг внутреннего статуса джобы в пользовательский этап (§68). */
export function stageForJobStatus(status: JobStatus): GenerationStage | null {
  switch (status) {
    case JobStatus.QUEUED:
    case JobStatus.PREPARING:
      return GenerationStage.PREPARING;
    case JobStatus.SUBMITTED:
    case JobStatus.PROCESSING:
      return GenerationStage.CREATING;
    case JobStatus.POSTPROCESSING:
      return GenerationStage.FINALIZING;
    default:
      return null;
  }
}

/** Типы сцен (§19). */
export const SceneType = {
  PERSONA_SPEECH: 'persona_speech',
  DYNAMIC_PERSONA: 'dynamic_persona',
  BROLL: 'broll',
  IMAGE: 'image',
  UPLOADED_VIDEO: 'uploaded_video',
  MOTION_GRAPHICS: 'motion_graphics',
} as const;
export type SceneType = (typeof SceneType)[keyof typeof SceneType];

export const sceneTypeLabels: Record<SceneType, string> = {
  persona_speech: 'Сцена с вами',
  dynamic_persona: 'Динамичная сцена',
  broll: 'Видео-вставка',
  image: 'Изображение',
  uploaded_video: 'Загруженное видео',
  motion_graphics: 'Графика',
};

/** Форматы кадра (§15). */
export const AspectRatio = {
  R9X16: '9:16',
  R16X9: '16:9',
  R1X1: '1:1',
} as const;
export type AspectRatio = (typeof AspectRatio)[keyof typeof AspectRatio];

/** Типы записей в ledger (§38). */
export const LedgerType = {
  CREDIT: 'credit',
  RESERVE: 'reserve',
  DEBIT: 'debit',
  RELEASE: 'release',
  REFUND: 'refund',
  BONUS: 'bonus',
  ADJUSTMENT: 'adjustment',
} as const;
export type LedgerType = (typeof LedgerType)[keyof typeof LedgerType];

/** Коды операций из справочника OperationCost (§34). */
export const OperationCode = {
  AVATAR_CREATE: 'avatar_create',
  VOICE_CLONE: 'voice_clone',
  VOICE_CLONE_UPGRADE: 'voice_clone_upgrade',
  LOOK_GENERATE: 'look_generate',
  IMAGE_GENERATE: 'image_generate',
  AVATAR_VIDEO_SECOND: 'avatar_video_second',
  DYNAMIC_VIDEO_SECOND: 'dynamic_video_second',
  BROLL_SECOND: 'broll_second',
  MOTION_SECOND: 'motion_second',
  MUSIC_SECOND: 'music_second',
  LIP_SYNC_SECOND: 'lip_sync_second',
  FINAL_RENDER_SECOND: 'final_render_second',
} as const;
export type OperationCode = (typeof OperationCode)[keyof typeof OperationCode];

/** Типы согласий (§4). */
export const ConsentType = {
  TERMS_OF_SERVICE: 'terms_of_service',
  PRIVACY_POLICY: 'privacy_policy',
  MEDIA_PROCESSING: 'media_processing',
  DIGITAL_LIKENESS: 'digital_likeness',
  VOICE_CLONING: 'voice_cloning',
} as const;
export type ConsentType = (typeof ConsentType)[keyof typeof ConsentType];

/** Очереди (agents/api-jobs-queues.md §3). */
export const QueueName = {
  MEDIA: 'media',
  GENERATION: 'generation',
  MOTION: 'motion',
  RENDER: 'render',
  MAINTENANCE: 'maintenance',
} as const;
export type QueueName = (typeof QueueName)[keyof typeof QueueName];

export const allQueueNames: QueueName[] = Object.values(QueueName);

/** Префиксы в S3 (§47). */
export const StoragePrefix = {
  SOURCE: 'source',
  GENERATED: 'generated',
  TEMP: 'temp',
  FINAL: 'final',
} as const;
export type StoragePrefix = (typeof StoragePrefix)[keyof typeof StoragePrefix];

/** Виды провайдеров (§42). Имена конкретных сервисов сюда не попадают (§41). */
export const ProviderKind = {
  AVATAR: 'avatar',
  VOICE: 'voice',
  IMAGE: 'image',
  DYNAMIC_VIDEO: 'dynamic_video',
  LIP_SYNC: 'lip_sync',
  MOTION: 'motion',
  /** §31: фоновая музыка под ролик (B3). */
  MUSIC: 'music',
  DIRECTOR: 'director',
} as const;
export type ProviderKind = (typeof ProviderKind)[keyof typeof ProviderKind];

/** Виды графики §25 — словами, а не кодом движка. */
export const graphicsKindLabels: Record<string, string> = {
  big_number: 'Крупная цифра',
  chart: 'График',
  list: 'Список',
  diagram: 'Схема',
  lower_third: 'Подпись в нижней трети',
  text_block: 'Текстовый блок',
  cta: 'Призыв к действию',
  logo: 'Логотип',
  ui_mock: 'Экран интерфейса',
  other: 'Графика',
};
